import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Video, Calendar, Clock, Users, ExternalLink, Plus, Trash2,
  Settings, CheckCircle, XCircle, Loader2, Play, Link2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('session_token')}`
});

const LiveClassesManagement = ({ professions, institutions }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [zoomConfigStatus, setZoomConfigStatus] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    topic: '',
    description: '',
    start_time: '',
    duration_minutes: 60,
    profession_id: '',
    institution_id: ''
  });
  
  // Zoom config state
  const [zoomConfig, setZoomConfig] = useState({
    account_id: '',
    client_id: '',
    client_secret: ''
  });
  
  useEffect(() => {
    loadMeetings();
  }, []);
  
  const loadMeetings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/zoom/meetings`,
        { headers: getAuthHeaders() }
      );
      setMeetings(response.data.meetings || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Error al cargar clases');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const checkZoomConfig = async (institutionId) => {
    try {
      const response = await axios.get(
        `${API}/zoom/config/institution/${institutionId}`,
        { headers: getAuthHeaders() }
      );
      setZoomConfigStatus(prev => ({
        ...prev,
        [institutionId]: response.data
      }));
    } catch (error) {
      console.error('Error checking zoom config:', error);
    }
  };
  
  const handleCreateMeeting = async () => {
    if (!formData.topic || !formData.start_time || !formData.profession_id) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/zoom/meetings`,
        formData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Clase creada correctamente');
      setShowCreateModal(false);
      setFormData({
        topic: '',
        description: '',
        start_time: '',
        duration_minutes: 60,
        profession_id: '',
        institution_id: ''
      });
      loadMeetings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear clase');
    }
  };
  
  const handleDeleteMeeting = async (meetingId) => {
    if (!confirm('¿Cancelar esta clase?')) return;
    
    try {
      await axios.delete(
        `${API}/zoom/meetings/${meetingId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Clase cancelada');
      loadMeetings();
    } catch (error) {
      toast.error('Error al cancelar clase');
    }
  };
  
  const handleConfigureZoom = async () => {
    if (!selectedInstitution || !zoomConfig.account_id || !zoomConfig.client_id || !zoomConfig.client_secret) {
      toast.error('Completa todos los campos');
      return;
    }
    
    try {
      await axios.post(
        `${API}/zoom/config/institution/${selectedInstitution}`,
        zoomConfig,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Zoom configurado correctamente');
      setShowConfigModal(false);
      setZoomConfig({ account_id: '', client_id: '', client_secret: '' });
      checkZoomConfig(selectedInstitution);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al configurar Zoom');
    }
  };
  
  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusBadge = (meeting) => {
    const now = new Date();
    const start = new Date(meeting.start_time);
    const end = new Date(start.getTime() + meeting.duration_minutes * 60000);
    
    if (meeting.status === 'cancelled') {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Cancelada</span>;
    }
    if (meeting.status === 'completed') {
      return <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">Finalizada</span>;
    }
    if (now >= start && now <= end) {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs animate-pulse">🔴 EN VIVO</span>;
    }
    return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Programada</span>;
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Clases en Vivo</h2>
          <p className="text-slate-600">Gestiona las clases de Zoom para instituciones</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConfigModal(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Configurar Zoom
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Clase
          </Button>
        </div>
      </div>
      
      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-medium text-blue-900 mb-2">ℹ️ Integración con Zoom</h3>
        <p className="text-sm text-blue-700">
          Para usar las clases en vivo, cada institución debe configurar sus credenciales de Zoom 
          (Server-to-Server OAuth). Puedes obtenerlas en{' '}
          <a href="https://marketplace.zoom.us" target="_blank" rel="noopener noreferrer" className="underline">
            marketplace.zoom.us
          </a>
        </p>
      </div>
      
      {/* Meetings List */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Video className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No hay clases programadas</p>
          <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Crear primera clase
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {meetings.map(meeting => (
            <div
              key={meeting.meeting_id}
              className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg">{meeting.topic}</h3>
                    {getStatusBadge(meeting)}
                  </div>
                  
                  {meeting.description && (
                    <p className="text-slate-600 text-sm mb-3">{meeting.description}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(meeting.start_time)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {meeting.duration_minutes} min
                    </div>
                    {meeting.attendees?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {meeting.attendees.length} asistentes
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {meeting.status === 'scheduled' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(meeting.start_url || meeting.join_url, '_blank')}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Iniciar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(meeting.join_url);
                          toast.success('Enlace copiado');
                        }}
                      >
                        <Link2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMeeting(meeting.meeting_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Create Meeting Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-xl font-bold mb-4">Nueva Clase en Vivo</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Título *</label>
                <Input
                  value={formData.topic}
                  onChange={(e) => setFormData({...formData, topic: e.target.value})}
                  placeholder="Ej: Clase de repaso - Tema 5"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                  rows={2}
                  placeholder="Descripción opcional"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Fecha y hora *</label>
                  <Input
                    type="datetime-local"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Duración (min)</label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Profesión *</label>
                <select
                  value={formData.profession_id}
                  onChange={(e) => setFormData({...formData, profession_id: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">Seleccionar profesión</option>
                  {professions.map(p => (
                    <option key={p.profession_id} value={p.profession_id}>{p.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Institución (opcional)</label>
                <select
                  value={formData.institution_id}
                  onChange={(e) => setFormData({...formData, institution_id: e.target.value})}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">Clase pública (todos los estudiantes)</option>
                  {institutions?.map(i => (
                    <option key={i.institution_id} value={i.institution_id}>{i.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Si seleccionas una institución, solo sus estudiantes verán la clase
                </p>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateMeeting}>
                <Video className="w-4 h-4 mr-2" />
                Crear Clase
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Zoom Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-xl font-bold mb-4">Configurar Zoom para Institución</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Institución</label>
                <select
                  value={selectedInstitution}
                  onChange={(e) => {
                    setSelectedInstitution(e.target.value);
                    if (e.target.value) checkZoomConfig(e.target.value);
                  }}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">Seleccionar institución</option>
                  {institutions?.map(i => (
                    <option key={i.institution_id} value={i.institution_id}>{i.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedInstitution && zoomConfigStatus[selectedInstitution]?.configured && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Zoom ya está configurado</span>
                  </div>
                  <p className="text-sm text-green-600 mt-1">
                    Configurado el {new Date(zoomConfigStatus[selectedInstitution].configured_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p className="font-medium mb-2">Cómo obtener las credenciales:</p>
                <ol className="list-decimal list-inside space-y-1 text-slate-600">
                  <li>Ve a <a href="https://marketplace.zoom.us" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">marketplace.zoom.us</a></li>
                  <li>Crea una app de tipo "Server-to-Server OAuth"</li>
                  <li>Copia el Account ID, Client ID y Client Secret</li>
                  <li>Activa los permisos de "meeting:write"</li>
                </ol>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Account ID</label>
                <Input
                  value={zoomConfig.account_id}
                  onChange={(e) => setZoomConfig({...zoomConfig, account_id: e.target.value})}
                  placeholder="Ej: AbCdEfG123..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Client ID</label>
                <Input
                  value={zoomConfig.client_id}
                  onChange={(e) => setZoomConfig({...zoomConfig, client_id: e.target.value})}
                  placeholder="Ej: XyZ789..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Client Secret</label>
                <Input
                  type="password"
                  value={zoomConfig.client_secret}
                  onChange={(e) => setZoomConfig({...zoomConfig, client_secret: e.target.value})}
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowConfigModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleConfigureZoom} disabled={!selectedInstitution}>
                <Settings className="w-4 h-4 mr-2" />
                Guardar Configuración
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveClassesManagement;
